import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AddCompanyModal from "../../../components/modals/AddCompanyModal";
import ChangeBrandModal from "../../../components/modals/ChangeBrandModal";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Loading from "../../../components/Loading";
import { DashboardLayout } from "../../../components/organisms/Layout";
import { selectRoles } from "../../../store/authSlice";
import Stepper from "../../../components/molecules/FormStepper/Stepper";
import StepperControl from "../../../components/molecules/FormStepper/StepperControl";
import { UseContextProvider } from "../../../components/molecules/FormStepper/StepperContext";
import Payment from "../../admin/Settings/Steps/Payments";
import Final from "../../admin/Settings/Steps/Final";
import CompanyDetails from "../../admin/Settings/Steps/CompanyDetails";
import RegionDetails from "../../admin/Settings/Steps/RegionDetails";
import BrandDetails from "../../admin/Settings/Steps/BrandDetails";
import LocationDetails from "../../admin/Settings/Steps/LocationDetails";
import { SettingsContextProvider } from "./SettingsContext";
import SlideOverPanel from "../../../components/atoms/SildeOverPanel/SlideOverPanel";
import FinalTurnKeyConfirmation from "./Steps/FinalTurnKeyConfirmation";
import TurnKeyDetails from "./Steps/TurnKeyDetails";
import Preview from "./Steps/Preview";
import "./Settings.scss";

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [company, setCompany] = useState({ companyName: "", companyId: "" });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [showChangeBrandModal, setShowChangeBrandModal] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isTurnKeyAccount, setIsTurnKeyAccount] = useState(null);
  const [warningStatus, setWarningStatus] = useState(false);
  const formTopRef = React.createRef();
  const [currentStep, setCurrentStep] = useState(1);
  const [entryValidity, setEnterValidity] = useState(true);
  const roles = useSelector(selectRoles);

  const stepsInitial = [
    "Business Details",
    "Brand",
    "Region",
    "Location",
    "Public Profile",
    "Complete",
  ];

  const [steps, setSteps] = useState(stepsInitial);

  useEffect(() => {
    document.title = "HeyHire Portal-Settings";
    if (roles[0] !== "turnkey_admin" && roles[0] !== "super-user") {
      navigate("/dashboard");
    }
  }, []);

  const handleBusinessTypeUpdate = (businessTypeId) => {
    if (businessTypeId === null) {
      setIsTurnKeyAccount(null);
    } else {
      if (businessTypeId === 3) {
        const stepsTurnKey = [
          "TurnKey Account",
          "Account Details",
          "Confirmation",
        ];

        setIsTurnKeyAccount(businessTypeId);
        setSteps(stepsTurnKey);
      } else {
        setIsTurnKeyAccount(null);
        setSteps(stepsInitial);
      }
    }
  };

  const companyValidationCheck = () => {
    if (isTurnKeyAccount === null) {
      return false;
    } else {
      return true;
    }
  };

  const displayStep = (step) => {
    switch (step) {
      case 1:
        return (
          <CompanyDetails
            handleBusinessTypeUpdate={handleBusinessTypeUpdate.bind(this)}
          />
        );
      case 2:
        if (isTurnKeyAccount) {
          return (
            <TurnKeyDetails
              setTurnKeyObjectEntry={getTurnKeyObjectEntry}
              error={!entryValidity}
            />
          );
        }
        return <BrandDetails />;

      case 3:
        if (!entryValidity) {
          setCurrentStep(2);
          return;
        }

        if (isTurnKeyAccount) {
          return <FinalTurnKeyConfirmation />;
        }
        return <RegionDetails company={company} />;
      case 4:
        return <LocationDetails />;
      case 5:
        return <Payment />;
      case 6:
        return <Final />;
      default:
    }
  };

  const getTurnKeyObjectEntry = (
    object,
    imageUploadFile = false,
    brandLogoUUID = false
  ) => {
    if (
      object.turnKeyLocationName &&
      object.turnKeyLocationCategory &&
      object.turnKeyLocationUrlSlug &&
      object.turnKeyBrandName &&
      object.turnKeyBrandUrlSlug &&
      object.turnKeyWebsite &&
      object.turnKeyBrandDescription &&
      object.turnKeyCity &&
      object.turnKeyState &&
      object.turnKeyZip &&
      (brandLogoUUID || (imageUploadFile.length !== 0 && imageUploadFile))
    ) {
      setEnterValidity(true);
    } else {
      setEnterValidity(false);
    }
  };

  const handleClick = async (direction) => {
    if (!companyValidationCheck()) {
      setWarningStatus(true);
      formTopRef.current.scrollIntoView({ behavior: "smooth" });
    } else {
      setWarningStatus(false);
      setLoading(true);

      try {
        let newStep = currentStep;
        direction === "next" ? newStep++ : newStep--;

        if (newStep > 0 && newStep <= steps.length) {
          // Simulate a small delay for smooth transition
          await new Promise((resolve) => setTimeout(resolve, 300));
          setCurrentStep(newStep);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <DashboardLayout >
      <>
        {showModal && (
          <AddCompanyModal
            company={editCompany}
            title={
              editCompany
                ? t("companies.edit-company")
                : t("companies.add-a-company")
            }
            onClose={() => setShowModal(!showModal)}
            visible={showModal}
          />
        )}
        {showChangeBrandModal && (
          <ChangeBrandModal
            company={editCompany}
            title="Change Brand"
            onClose={() => setShowChangeBrandModal(!showChangeBrandModal)}
            visible={showChangeBrandModal}
          />
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div>
              <h2
                className="text-2xl font-bold text-gray-900"
                ref={formTopRef}
              >
                Settings
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Configure your business settings and manage your account
                preferences.
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white">
            <SettingsContextProvider>
              {/* Stepper */}
              <div className="px-6 py-6 border-b border-gray-200">
                <Stepper
                  key={isTurnKeyAccount}
                  steps={steps}
                  currentStep={currentStep}
                />
              </div>

              {/* Warning Alert */}
              {warningStatus && (
                <div className="px-6 pt-6">
                  <div className="rounded-lg bg-yellow-50">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Selection Required
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>Select turnkey account or Create a new one</p>
                          <p className="mt-1 font-medium">
                            NOTE: Only turnkey feature is enabled
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Form Content */}
              <UseContextProvider>
                <div className="p-6">
                  <div className="bg-gray-50">
                    <div className="relative">
                      {/* Form Content */}
                      <div
                        className={`transition-opacity duration-300 ${
                          loading ? "opacity-50" : "opacity-100"
                        }`}
                      >
                        <div className="max-w-3xl mx-auto p-6">
                          {displayStep(currentStep)}
                        </div>
                      </div>

                      {/* Loading Overlay */}
                      {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                          <div className="flex flex-col items-center">
                            <Loading />
                            <span className="mt-4 text-sm text-gray-600">
                              Loading...
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </UseContextProvider>

              {isPanelOpen && (
                <SlideOverPanel
                  title="Business Details"
                  isOpen={isPanelOpen}
                  onClose={() => setIsPanelOpen(false)}
                >
                  <Preview />
                </SlideOverPanel>
              )}

              {/* Validation Alert */}
              {!entryValidity && (
                <div className="px-6 pb-6">
                  <div className="rounded-lg bg-red-50">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Incomplete Form
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>
                            Please fill in all required fields. Only the
                            position field is optional.
                          </p>
                          <button
                            onClick={() =>
                              formTopRef.current.scrollIntoView({
                                behavior: "smooth",
                              })
                            }
                            className="mt-2 text-red-800"
                          >
                            <svg
                              className="w-4 h-4 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 10l7-7m0 0l7 7m-7-7v18"
                              />
                            </svg>
                            Go to top
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="px-6 py-6 bg-gray-50">
                {currentStep !== steps.length && (
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => handleClick("prev")}
                      className={`rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900
                        currentStep === 1 ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      disabled={currentStep === 1}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handleClick("next")}
                      className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
                    >
                      {loading ? (
                        <i className="fad fa-circle-notch fa-spin" />
                      ) : (
                        "Next"
                      )}
                    </button>
                  </div>
                )}
              </div>
            </SettingsContextProvider>
          </div>
        </div>
      </>
    </DashboardLayout>
  );
};

export default Settings;
